// Tells the user, once, what happened at sync: final IDs given, changes that could not be saved,
// or that signal is back and signing in will send what is waiting.
import { useEffect, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { useToast } from "../ui/toast";
import { useAuth } from "../auth/AuthProvider";
import { useOnline } from "./useOnline";
import { waitingCount } from "./waiting";
import type { SyncOutcome } from "./sync";
import { Button } from "../ui/primitives";
import { startAuthentication } from "@simplewebauthn/browser";
import { callFunction } from "../lib/api";
import { sendHeldWithPasskey } from "./sync";
import { changedSummaries, summaryText } from "./picks";

/** "2 saved changes", "2 files on this phone", or both. */
function savedText(changes: number, files: number): string {
  return [changes ? `${changes} saved ${changes === 1 ? "change" : "changes"}` : "", files ? `${files} ${files === 1 ? "file" : "files"} on this phone` : ""].filter(Boolean).join(" and ");
}

export function SyncNotices() {
  const toast = useToast();
  const { offline, signInWithPasskey } = useAuth();
  const online = useOnline();
  // Decision 84 #1: files still on the phone are waiting too; round 3: a file a waiting change refers to is part of it
  // (the same count as the sync indicator).
  const { changes: waiting, files } = useLiveQuery(waitingCount, [], { changes: 0, files: 0 });
  const [busy, setBusy] = useState(false);
  const [revoked, setRevoked] = useState(false);
  const [heldSent, setHeldSent] = useState(false);
  useEffect(() => {
    const on = (e: Event) => {
      const o = (e as CustomEvent<SyncOutcome>).detail;
      if (o.ids.length) toast(o.ids.length === 1 ? `Final ID ${o.ids[0]!.register_id} given to ${o.ids[0]!.temp_id}.` : `${o.ids.length} animals received their final IDs.`);
      if (o.proposed) toast(`${o.proposed === 1 ? "Your report was" : `${o.proposed} reports were`} sent to a general manager for approval. Nothing changes until it is approved.`, "info");
      if (o.rejected) toast(`${o.rejected} ${o.rejected === 1 ? "change" : "changes"} could not be saved; see Tasks.`, "danger");
      if (o.held) toast("Your changes are waiting for the CEO.", "info");
      // Decision 84 #1: each picked set whose files were tried this round, counted whole from what the server confirmed.
      // Round 2 (F6 a): only a set whose counts changed since its summary was last shown.
      void changedSummaries(o.selections ?? []).then((fresh) => {
        for (const s of fresh) toast(summaryText(s), s.failed || s.not_kept ? "danger" : s.waiting ? "info" : "success");
      }, (err: unknown) => {
        // The memory of what was shown could not be read: show the summaries rather than nothing.
        console.warn(`Upload summaries: ${err instanceof Error ? err.message : String(err)}`);
        for (const s of o.selections ?? []) toast(summaryText(s), s.failed || s.not_kept ? "danger" : s.waiting ? "info" : "success");
      });
    };
    window.addEventListener("rk:synced", on);
    return () => window.removeEventListener("rk:synced", on);
  }, [toast]);
  if (!(offline && online)) return null;
  return (
    <div role="status" className="sticky top-0 z-30 flex flex-wrap items-center justify-center gap-3 bg-primary px-4 py-2 text-[15px] text-on-primary">
      <span>Signal is back.{waiting || files ? ` Sign in to send your ${savedText(waiting, files)}.` : " Sign in to continue."}</span>
      {heldSent ? <span>Your saved changes were sent and are waiting for the CEO.</span> : revoked ? (
        <>
          <span>Your login has been switched off.</span>
          <Button variant="sand" busy={busy} onClick={async () => {
            setBusy(true);
            try {
              const opts = await callFunction<{ challenge_id: string; options: any }>("passkey/login-options", {}, { auth: false });
              const response = await startAuthentication({ optionsJSON: opts.options });
              const r = await sendHeldWithPasskey({ challenge_id: opts.challenge_id, response });
              if (!r.error) setHeldSent(true);
            } finally { setBusy(false); }
          }}>Send my saved changes to the CEO</Button>
        </>
      ) : (
        <Button variant="sand" busy={busy} onClick={async () => {
          setBusy(true);
          try { await signInWithPasskey(); } catch (e) { if (/switched off/i.test((e as Error).message)) setRevoked(true); } finally { setBusy(false); }
        }}>Sign in</Button>
      )}
    </div>
  );
}
